import { computed, action } from 'mobx';
import { StoreBase } from '../../../common/StoreBase';
import { IChartPeriodSelectionConfiguration } from '../models';
import { IChartPeriodSelectionProps } from '@kurtosys/ksys-app-components/dist/components/charting/ChartPeriodSelection/models';
import { LineChartStore } from '../../LineChart/stores/LineChartStore';
import { StoreContext } from '../../../configuration/StoreContext';
import { TPeriods } from '@kurtosys/ksys-app-components/dist/components/charting/ChartPeriodSelection/models/TPeriods';
import { utils } from '@kurtosys/ksys-app-template/dist';

export class ChartPeriodSelectionStore extends StoreBase {
	static componentKey: 'chartPeriodSelection' = 'chartPeriodSelection';

	constructor(storeContext: StoreContext) {
		super(storeContext);
		this.handleOnClick = this.handleOnClick.bind(this);
	}

	@computed
	get configuration(): IChartPeriodSelectionConfiguration | undefined {
		if (this.storeContext && this.storeContext.appStore) {
			return this.storeContext.appStore.getComponentConfiguration(
				ChartPeriodSelectionStore.componentKey
			);
		}
	}

	@action
	async initialize(): Promise<void> { }

	@computed
	get lineChartStore(): LineChartStore {
		return this.storeContext && this.storeContext.lineChartStore;
	}

	handleOnClick(response: any) {
		if (this.lineChartStore) {
			this.lineChartStore.selectChartPeriod(
				response.timestamp,
				response.selectedPeriodLabel
			);

			const { analyticsHelper } = this.storeContext.appStore;
			if (analyticsHelper && response.logDataLayer) {
				// Once the selectChartPeriod has done its deed,
				// we can pull the start and end dates off of the periodData as it will filter the data set,
				// this will align the logging dates with the data points in the DB for easier correlation
				const periodData = this.lineChartStore.periodData;
				let dataStartDate;
				let dataEndDate;
				if (periodData && utils.isArrayLike.isArrayLike(periodData)) {
					dataStartDate = periodData[0].date;
					dataEndDate = periodData[periodData.length - 1].date;
				} else {
					console.warn(
						`Chart Period Selection Store: handleOnClick, could not pull the data start and data end dates from ${ periodData }. Expecting an array`
					);
				}
				analyticsHelper.logEvent({
					event: 'period_selection_click',
					eventType: 'click',
					context: {
						startDate: dataStartDate,
						endDate: dataEndDate,
						periodName: response.selectedPeriodLabel,
					},
				});
			}
		}
	}

	@computed
	get periods(): TPeriods {
		const chartPeriodSelectionProps = this.configuration;
		const availablePeriods: TPeriods = {};
		const periodicity = this.lineChartStore.dataPeriodicity;
		if (chartPeriodSelectionProps) {
			const { periods } = chartPeriodSelectionProps;
			if (periods) {
				for (const period of periods) {
					const { customKey } = period;
					if (
						this.isPeriodAllowed(
							periodicity,
							customKey ? customKey.period : period.key
						)
					) {
						const key = customKey
							? `${ customKey.interval }${ customKey.period }`
							: period.key || '';
						availablePeriods[key] = period.value;
					}
				}
			}
		}
		return availablePeriods;
	}

	@computed
	get startDateFallBackDirection() {
		const chartPeriodSelectionProps = this.configuration;
		if (chartPeriodSelectionProps) {
			return chartPeriodSelectionProps.startDateFallBackDirection || "forwards";
		}
	}

	isPeriodAllowed(periodicity: string, periodKey: string | undefined): boolean {
		const periodOrder = ['d', 'w', 'm', 'y'];
		if (periodKey === 'YTD' || periodKey === 'ALL') {
			return true;
		}
		if (periodKey) {
			const periodicitySymbol = periodicity.charAt(0).toLowerCase();
			const periodSymbol = periodKey.charAt(periodKey.length - 1).toLowerCase();
			return (
				periodOrder.indexOf(periodSymbol) >=
				periodOrder.indexOf(periodicitySymbol)
			);
		}
		return false;
	}

	@computed
	get chartPeriodSelectionProps(): IChartPeriodSelectionProps | undefined {
		const chartPeriodSelectionProps = this.configuration;
		if (chartPeriodSelectionProps) {
			const { dataDates } = this.lineChartStore;
			if (dataDates) {
				const { startDate, endDate } = dataDates;
				return {
					startDate: new Date(startDate || 0).toISOString(),
					endDate: new Date(endDate || 0).toISOString(),
					defaultPeriod: chartPeriodSelectionProps.defaultPeriod,
					periods: this.periods,
					onClick: this.handleOnClick,
					currentStartDate: new Date(
						this.lineChartStore.startDate || 0
					).toISOString(),
					currentEndDate: new Date(
						this.lineChartStore.endDate || 0
					).toISOString(),
					shouldCalculateSelectedPeriod:
						this.lineChartStore.shouldCalculateSelectedPeriod,
				};
			}
		}
		return undefined;
	}
}
